import React, { useEffect } from "react";
import Vimeo from "@u-wave/react-vimeo";
import { useState, useRef } from "react";
import { Col } from "antd";
import {
  CaretRightOutlined,
  AudioMutedOutlined,
  ExpandOutlined,
  PauseOutlined,
  AudioOutlined,
} from "@ant-design/icons";
import { RiVolumeUpLine, RiVolumeMuteLine } from "react-icons/ri";
import videos from "../../../services/videos";

const VideosPlayer = ({ videoUrl, title }) => {
  const [isPlaying, setisPlaying] = useState(false);
  const [isMuted, setisMuted] = useState(false);
  const videoRef = useRef(null);
  const [videoIframe, setVideoIframe] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const containerRef = useRef(null);

  const getVideoUrl = async () => {
    try {
      const res = await videos.getVideoById({ url: videoUrl });
      setVideoIframe(res?.data?.video?.player_embed_url);
      setHasLoaded(true);
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setIsInView(true);
          if (videoUrl) {
            getVideoUrl();
          }
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: "50px", // Start loading 50px before the element enters viewport
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [videoUrl, hasLoaded]);

  return (
    <Col className="p-0" ref={containerRef}>
      <div className="relative w-100">
        {!isInView && !hasLoaded ? (
          <div 
            style={{ 
              position: "relative", 
              paddingBottom: "56.25%", 
              height: 0,
              backgroundColor: "#f0f0f0",
              borderRadius: "10px",
              border: "1px solid var(--border-color)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <div style={{ 
              position: "absolute", 
              top: "50%", 
              left: "50%", 
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#666"
            }}>
              <CaretRightOutlined style={{ fontSize: "48px", marginBottom: "10px" }} />
              <div>Video will load when visible</div>
            </div>
          </div>
        ) : videoIframe ? (
          <div
            style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}
          >
            {isError ? (
              <div style={{ 
                position: "absolute", 
                top: "50%", 
                left: "50%", 
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                color: "#666"
              }}>
                {/* Loading Animation for Error State */}
                <div style={{
                  width: "60px",
                  height: "60px",
                  margin: "0 auto 15px",
                  position: "relative"
                }}>
                  <div 
                    className="video-loading-spinner"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "4px solid #ffe7e7",
                      borderTop: "4px solid #ff4d4f",
                      borderRadius: "50%"
                    }}
                  ></div>
                  <CaretRightOutlined style={{ 
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "24px",
                    color: "#ff4d4f"
                  }} />
                </div>
                <div 
                  className="video-loading-text"
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ff4d4f"
                  }}
                >
                  Video is preparing...
                </div>
              </div>
            ) : (
              <Vimeo
                video={videoIframe}
                autoplay={false}
                showTitle={false}
                showByline={false}
                showPortrait={false}
                responsive={true}
                onError={(error) => setIsError(true)}
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  objectFit: "cover",
                  borderRadius: "10px",
                  border: "1px solid var(--border-color)",
                }}
              />
            )}
          </div>
        ) : isInView ? (
          <div 
            style={{ 
              position: "relative", 
              paddingBottom: "56.25%", 
              height: 0,
              backgroundColor: "#f8f9fa",
              borderRadius: "10px",
              border: "1px solid var(--border-color)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden"
            }}
          >
            <div style={{ 
              position: "absolute", 
              top: "50%", 
              left: "50%", 
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#666"
            }}>
              {/* Loading Animation */}
              <div style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 15px",
                position: "relative"
              }}>
                <div 
                  className="video-loading-spinner"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "4px solid #e9ecef",
                    borderTop: "4px solid #1890ff",
                    borderRadius: "50%"
                  }}
                ></div>
                <CaretRightOutlined style={{ 
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "24px",
                  color: "#1890ff"
                }} />
              </div>
              <div 
                className="video-loading-text"
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#666"
                }}
              >
                Loading Video...
              </div>
            </div>
            {/* Add CSS animations to head */}
            <style dangerouslySetInnerHTML={{
              __html: `
                .video-loading-spinner {
                  animation: videoSpinAnimation 1s linear infinite;
                }
                .video-loading-text {
                  animation: videoPulseAnimation 1.5s ease-in-out infinite;
                }
                @keyframes videoSpinAnimation {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes videoPulseAnimation {
                  0%, 100% { opacity: 0.6; }
                  50% { opacity: 1; }
                }
              `
            }} />
          </div>
        ) : null}
      </div>
    </Col>
  );
};

export default VideosPlayer;
